import React from 'react';
import { Icon } from '../icons';
import type { IconSize } from '../icons';
import { cn } from '@shared/lib/utils';

export interface StarsProps {
  /**
   * Number of stars to display
   * @default 5
   */
  count?: number;
  /**
   * Size of the stars
   * @default 'sm'
   */
  size?: IconSize;
  /**
   * Gap between stars
   * @default 'gap-0.5'
   */
  gap?: 'gap-0' | 'gap-0.5' | 'gap-1' | 'gap-1.5' | 'gap-2';
  /**
   * Color of the stars (Tailwind color class from design system)
   * @default 'text-feedback-success'
   */
  color?: string;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Whether to show filled/empty stars based on rating
   * If provided, stars will be filled proportionally to the rating
   */
  rating?: number;
  /**
   * Color for empty stars when using rating prop
   * @default 'text-border-secondary'
   */
  emptyColor?: string;
}

/**
 * Stars component
 *
 * Displays a row of star icons with customizable size, gap, and color.
 * Can display filled/empty stars based on rating value.
 *
 * @example
 * ```tsx
 * // Simple stars
 * <Stars count={5} size="md" />
 *
 * // Stars with rating (filled/empty)
 * <Stars count={5} rating={3.5} size="lg" />
 *
 * // Custom colors and gap
 * <Stars
 *   count={5}
 *   size="sm"
 *   gap="gap-1"
 *   color="text-yellow-500"
 *   emptyColor="text-gray-200"
 * />
 * ```
 */
export const Stars: React.FC<StarsProps> = ({
  count = 5,
  size = 'sm',
  gap = 'gap-0.5',
  color = 'text-feedback-success',
  emptyColor = 'text-border-secondary',
  rating,
  className,
}) => {
  const stars = Array.from({ length: count }, (_, index) => index);

  return (
    <div className={cn('flex items-center', gap, className)} role="img" aria-label={rating ? `Rating: ${rating} out of ${count}` : `${count} stars`}>
      {stars.map((index) => {
        // Determine if this star should be filled based on rating
        const isFilled = rating !== undefined ? index < Math.round(rating) : true;

        return (
          <Icon
            key={index}
            name="star"
            size={size}
            className={isFilled ? color : emptyColor}
            aria-hidden="true"
          />
        );
      })}
    </div>
  );
};
